import { useState, useEffect, useRef, useCallback } from 'react';
import { showAlert } from '@/components/AppDialog';
import {
  Bell, Send, Users, AlertCircle, MessageSquare, CheckCheck,
  Search, Mail, Paperclip, Eye, Smile, Link2, User2, ChevronDown,
  Bold, Italic, Underline, List, ListOrdered, Minus, Image, X,
  FileText, Upload, Inbox, Clock, ChevronRight, Download,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import UserPicker from '../components/UserPicker';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';

type NotifUser = { uid?: string; _id?: string; firstName: string; lastName: string; email: string; };

type Attachment = { name: string; url: string; type: string; size: number; uploading?: boolean; };

type SentEntry = {
  to: string; title: string; message: string; sentAt: string;
  type: string; priority: string; count: number;
};


const PRIORITY_CONFIG = {
  CRITICAL: { label: '! Critical', bg: '#FEF2F2', color: '#DC2626', border: '#EF4444' },
  HIGH:     { label: '↑ High',     bg: '#FFF7ED', color: '#EA580C', border: '#FB923C' },
  NORMAL:   { label: 'Normal',     bg: '#F0F9FF', color: '#0369A1', border: '#93C5FD' },
};

const TYPE_ICON_BG: Record<string, { bg: string; color: string }> = {
  alert:   { bg: '#FEF2F2', color: '#DC2626' },
  message: { bg: '#EFF6FF', color: '#2563EB' },
  email:   { bg: '#F3F4F6', color: '#6B7280' },
  file:    { bg: '#F0FDFA', color: '#0F766E' },
};

const TYPE_ICON_EL: Record<string, React.ReactNode> = {
  alert:   <AlertCircle className="w-5 h-5" />,
  message: <MessageSquare className="w-5 h-5" />,
  email:   <Mail className="w-5 h-5" />,
  file:    <Paperclip className="w-5 h-5" />,
};

const EMOJI_PRESETS = ['📢', '🚀', '⚡', '✅', '⚠️', '📋', '🎯', '💡', '🔔', '🏆', '📌', '🔐'];
const SENDER_ROLE_PRESETS = ['Admin Team', 'Business Advisor', 'Compliance Team', 'Support Team', 'Onboarding Team', 'Finance Team'];

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ── Day bucketing for the redesigned feed ──
function dayBucket(iso: string): 'Today' | 'Yesterday' | 'Earlier' {
  if (!iso) return 'Earlier';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Earlier';
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = d.getTime();
  if (t >= startToday) return 'Today';
  if (t >= startToday - 86400000) return 'Yesterday';
  return 'Earlier';
}
const BUCKET_ORDER: Array<'Today' | 'Yesterday' | 'Earlier'> = ['Today', 'Yesterday', 'Earlier'];
function groupByDay<T>(items: T[], getIso: (x: T) => string): Array<{ label: string; items: T[] }> {
  const map: Record<string, T[]> = { Today: [], Yesterday: [], Earlier: [] };
  for (const it of items) map[dayBucket(getIso(it))].push(it);
  return BUCKET_ORDER.filter(b => map[b].length).map(b => ({ label: b, items: map[b] }));
}

const CSS = `
/* Page-specific only — the top bar, tabs and tokens come from adminShell.css. */

/* cards */
.adminx .ncard{background:#fff;border:1px solid #E6E9ED;border-radius:16px;box-shadow:0 1px 2px rgba(15,23,42,.05);overflow:hidden}.adminx .card-h .ic{color:var(--brand);display:flex}.adminx .card-h .sub{font-size:12.5px;color:#94A3B8;font-weight:500}
.adminx .card-h .spacer{margin-left:auto}/* stat cards */
/* filter chips — the shared .chips row is edge-to-edge, so inset it to the card gutter (matches .card-h) */
.adminx .ncard > .chips{padding:14px 20px;align-items:center}
.adminx .markread{margin-left:auto;background:none;border:none;font:inherit;font-size:12.5px;font-weight:600;color:var(--brand);cursor:pointer;display:inline-flex;align-items:center;gap:6px;padding:6px 4px}
.adminx .markread:hover{text-decoration:underline}
.adminx .markread:disabled{color:#B6BECB;cursor:not-allowed;text-decoration:none}

/* feed */
.adminx .feed{padding:4px 12px 14px}
.adminx .daygrp{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.09em;color:#94A3B8;padding:14px 8px 6px}
.adminx .nrow{position:relative;display:flex;align-items:flex-start;gap:13px;padding:12px;border-radius:12px;transition:.12s;text-align:left;width:100%;border:none;background:transparent;font:inherit;color:inherit;cursor:pointer}
.adminx .nrow:hover{background:#F5F7FA}
.adminx .nrow.plain{cursor:default}
.adminx .nrow.plain:hover{background:transparent}
.adminx .nrow.unread{background:#F1F6FF}
.adminx .nrow.unread:hover{background:#E7F0FF}
.adminx .nrow.unread::before{content:"";position:absolute;left:0;top:10px;bottom:10px;width:3px;border-radius:0 3px 3px 0;background:var(--brand)}
.adminx .nrow.open{background:#F1F6FF}
.adminx .nicon{width:34px;height:34px;border-radius:10px;display:grid;place-items:center;flex:none}
.adminx .nicon.msg{background:#EFF6FF;color:#2563EB}
.adminx .nicon.alert{background:#FEF2F2;color:#DC2626}
.adminx .nicon.email{background:#F5F3FF;color:#7C3AED}
.adminx .nicon.file{background:#F0FDFA;color:#0F766E}
.adminx .nicon.sys{background:#F1F5F9;color:#475569}
.adminx .nmain{flex:1;min-width:0}
.adminx .ntop{display:flex;align-items:flex-start;justify-content:space-between;gap:10px}
.adminx .nrtitle{font-size:13.5px;font-weight:600;color:#0F172A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.adminx .nrow.unread .nrtitle{font-weight:700}
.adminx .ntime{font-size:11.5px;color:#94A3B8;white-space:nowrap;flex:none;display:inline-flex;align-items:center;gap:4px}
.adminx .nsubj{font-size:12.5px;font-weight:600;color:#334155;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.adminx .nbody{font-size:12.5px;color:#64748B;margin-top:2px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
.adminx .nmeta{display:flex;align-items:center;flex-wrap:wrap;gap:10px;margin-top:7px}
.adminx .ntag{font-size:11px;font-weight:600;color:#94A3B8;display:inline-flex;align-items:center;gap:4px}
.adminx .ntag.blue{color:var(--brand)}
.adminx .prio{font-size:10px;font-weight:800;padding:2px 8px;border-radius:6px;letter-spacing:.02em}
.adminx .prio.CRITICAL{background:#FEF2F2;color:#DC2626}
.adminx .prio.HIGH{background:#FFF7ED;color:#EA580C}
.adminx .prio.NORMAL{background:#EFF6FF;color:#2563EB}
.adminx .nchev{width:16px;height:16px;color:#B6BECB;flex:none;align-self:center;transition:transform .15s}
.adminx .nchev.rot{transform:rotate(90deg)}
.adminx .nexp{padding:2px 14px 16px 14px}/* empty + loading */
.adminx .empty .ei{width:56px;height:56px;border-radius:16px;background:#F1F5F9;color:#94A3B8;display:grid;place-items:center;margin:0 auto 14px}
.adminx .empty b{font-size:14px;font-weight:600;color:#475569;display:block}
.adminx .empty p{font-size:12.5px;color:#94A3B8;margin-top:4px}@keyframes adx-spin{to{transform:rotate(360deg)}}

/* composer toolbar */
.adminx .toolbar{display:flex;align-items:center;gap:2px;flex-wrap:wrap;padding:7px 9px;border:1px solid #D1D5DB;border-bottom:none;border-radius:11px 11px 0 0;background:#F8FAFC}
.adminx .tbtn{min-width:30px;height:30px;border-radius:7px;border:none;background:transparent;color:#64748B;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:.12s;font:inherit;padding:0 5px}
.adminx .tbtn:hover{background:#E8ECF1;color:#0F172A}
.adminx .tsep{width:1px;height:18px;background:#DDE2E9;margin:0 4px}/* user directory */
.adminx .udir{display:grid;grid-template-columns:1fr 1fr;gap:8px;max-height:420px;overflow-y:auto;padding-right:2px}
@media(max-width:640px){.adminx .udir{grid-template-columns:1fr}}
.adminx .urow{display:flex;align-items:center;gap:11px;padding:10px 12px;border:1px solid #EFF1F4;border-radius:11px;background:#FAFBFC;min-width:0}
.adminx .urow:hover{border-color:var(--brand-soft)}
.adminx .uav{width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,var(--brand),var(--brand-lt));color:#fff;display:grid;place-items:center;font-size:12px;font-weight:700;flex:none}
.adminx .urow .un{font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.adminx .urow .ue{font-size:11.5px;color:#94A3B8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.adminx .umore{grid-column:1/-1;text-align:center;padding:11px;border:1px dashed #D7DDE5;border-radius:11px;color:#94A3B8;font-size:12.5px}

/* rich text editor internals */
.adminx [contenteditable]:empty:before{content:attr(data-placeholder);color:#9CA3AF;pointer-events:none}
.adminx [contenteditable] ul{list-style:disc;padding-left:1.25rem}
.adminx [contenteditable] ol{list-style:decimal;padding-left:1.25rem}
.adminx [contenteditable] hr{border:0;border-top:1px solid #E5E7EB;margin:.75rem 0}
`;

export default function AdminNotifications() {
  const [users, setUsers] = useState<NotifUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sentLog, setSentLog] = useState<SentEntry[]>(() => {
    try { return JSON.parse(localStorage.getItem('admin_sent_log') || '[]'); } catch { return []; }
  });
  const [userSearch, setUserSearch] = useState('');
  const [alsoEmail, setAlsoEmail] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({
    targetType: 'all' as 'all' | 'user',
    targetUid: '',
    emoji: '',
    title: '',
    subtitle: '',
    type: 'message' as 'alert' | 'message' | 'email',
    priority: 'NORMAL' as 'CRITICAL' | 'HIGH' | 'NORMAL',
    ctaLabel: '',
    ctaUrl: '',
    senderName: 'Conncct Admin',
    senderRole: 'Admin Team',
  });

  const set = (key: keyof typeof form, val: string) => setForm(f => ({ ...f, [key]: val }));

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    try { localStorage.setItem('admin_sent_log', JSON.stringify(sentLog.slice(0, 100))); } catch {}
  }, [sentLog]);

  const fetchUsers = async () => {
    try {
      const token = getToken();
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_BASE_URL}/auth/users/all`, { headers });
      if (res.ok) {
        const data = await res.json();
        const list = data.users || data || [];
        setUsers(Array.isArray(list) ? list : []);
      }
    } catch (e) {
      console.error('Error fetching users:', e);
    } finally {
      setLoading(false);
    }
  };

  // ── Rich-text editor commands ──
  const execCmd = useCallback((cmd: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value ?? undefined);
    setPreviewHtml(editorRef.current?.innerHTML || '');
  }, []);

  const onEditorInput = () => setPreviewHtml(editorRef.current?.innerHTML || '');

  // ── File upload ──
  const uploadFile = async (file: File) => {
    const tempId = `${Date.now()}_${file.name}`;
    setAttachments(a => [...a, { name: file.name, url: '', type: file.type, size: file.size, uploading: true }]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE_URL}/auth/upload-notification-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken() || ''}` },
        body: formData,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Upload failed');
      setAttachments(a => {
        const idx = a.findIndex(x => x.name === file.name && x.uploading);
        if (idx === -1) return a;
        const next = [...a];
        next[idx] = { name: json.name, url: json.url, type: json.type, size: json.size, uploading: false };
        return next;
      });
    } catch (e) {
      console.error('Upload error:', e);
      setAttachments(a => a.filter(x => !(x.name === file.name && x.uploading)));
      showAlert(`Failed to upload ${file.name}`);
    }
  };

  const onFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach(uploadFile);
    e.target.value = '';
  };

  const removeAttachment = (idx: number) => setAttachments(a => a.filter((_, i) => i !== idx));

  const filteredUsers = users.filter(u =>
    `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase())
  );

  const fullTitle = form.emoji ? `${form.emoji} ${form.title}` : form.title;
  const pc = PRIORITY_CONFIG[form.priority] || PRIORITY_CONFIG.NORMAL;
  const iconStyle = TYPE_ICON_BG[form.type] || TYPE_ICON_BG.message;
  const senderInitials = (form.senderName || 'A').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const handleSend = async () => {
    const messageHtml = editorRef.current?.innerHTML || '';
    if (!form.title.trim() || !messageHtml.replace(/<[^>]*>/g, '').trim()) {
      showAlert('Please fill in both title and message.');
      return;
    }
    if (form.targetType === 'user' && !form.targetUid) {
      showAlert('Please select a user.');
      return;
    }
    if (attachments.some(a => a.uploading)) {
      showAlert('Please wait for all files to finish uploading.');
      return;
    }

    setSending(true);
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken() || ''}`,
    };

    const notification: Record<string, any> = {
      type: form.type,
      priority: form.priority,
      title: fullTitle,
      preview: messageHtml,
      sender: form.senderName || 'Admin',
      senderRole: form.senderRole || 'Admin',
      timestamp: new Date().toISOString(),
      read: false,
    };
    if (form.subtitle.trim()) notification.subtitle = form.subtitle.trim();
    if (form.ctaLabel.trim() && form.ctaUrl.trim()) {
      notification.ctaLabel = form.ctaLabel.trim();
      notification.ctaUrl = form.ctaUrl.trim();
    }
    if (attachments.length > 0) {
      notification.attachments = attachments.map(({ uploading: _u, ...rest }) => rest);
    }

    const targets = form.targetType === 'all'
      ? users
      : users.filter(u => (u._id || u.uid) === form.targetUid);

    try {
      const uids = targets.map(u => u._id || u.uid).filter(Boolean) as string[];
      const emails = alsoEmail ? targets.map(u => u.email).filter(Boolean) : [];
      const resp = await fetch(`${API_BASE_URL}/auth/send-notification`, {
        method: 'POST', headers,
        body: JSON.stringify({ uids, notification, sendEmail: alsoEmail, emails }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || `Server responded ${resp.status}`);
      }
      const respData = await resp.json().catch(() => ({}));

      const toLabel = form.targetType === 'all'
        ? `All users (${targets.length})`
        : targets[0] ? `${targets[0].firstName} ${targets[0].lastName}` : 'Unknown';

      setSentLog(prev => [{
        to: toLabel, title: fullTitle, message: messageHtml.replace(/<[^>]*>/g, ' ').trim(),
        sentAt: new Date().toISOString(), type: form.type, priority: form.priority, count: targets.length,
      }, ...prev]);

      // Reset form
      if (editorRef.current) editorRef.current.innerHTML = '';
      setPreviewHtml('');
      setAttachments([]);
      setForm(f => ({ ...f, title: '', subtitle: '', emoji: '', ctaLabel: '', ctaUrl: '' }));
      const emailedNote = alsoEmail ? ` · emailed ${respData.emailed ?? 0}` : '';
      showAlert(`Notification sent to ${targets.length} user(s)${emailedNote}.`);
    } catch (e) {
      console.error(e);
      showAlert('Failed to send notification. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // ── Inbox state ──
  const [activeTab, setActiveTab] = useState<'compose' | 'inbox'>('compose');
  const [inboxMessages, setInboxMessages] = useState<any[]>([]);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [expandedMsgId, setExpandedMsgId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);

  // ── Feed reskin state (session-scoped read tracking + filter chips) ──
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [inboxFilter, setInboxFilter] = useState<'all' | 'unread' | 'attachments' | 'replies'>('all');
  const [sentFilter, setSentFilter] = useState<'all' | 'message' | 'alert' | 'email'>('all');

  /* Reply to an inbound client message. Two independent deliveries:
       1. into their chat thread, in the conversation they wrote from — that is
          where they asked, so it is where the answer belongs;
       2. as an in-app notification plus (if we have their email) an email copy,
          so they hear about it with the chat closed.

     Neither is allowed to sink the other. In particular the chat write is a
     newer endpoint than this page, so on a backend that predates it the call
     404s — that must cost the client their chat copy, not their answer. The
     alert says which channels actually carried it. */
  const sendReply = async (msg: any) => {
    const text = replyText.trim();
    if (!text) { showAlert('Write a reply first.'); return; }
    if (!msg.senderUid) { showAlert('This message has no sender account to reply to.'); return; }
    setReplyBusy(true);

    const token = getToken();
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` };
    const post = async (path: string, body: any) => {
      const res = await fetch(`${API_BASE_URL}/auth/${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
      const json = await res.json().catch(() => null);
      if (!res.ok || (json && json.success === false)) throw new Error(json?.error || `${path} failed (${res.status})`);
      return json;
    };

    let chatError = '';
    let notifError = '';

    try {
      await post('message-reply', {
        uid: msg.senderUid,
        message: text,
        subject: msg.subject || null,
        conversationId: msg.conversationId || null,
        replyToMessageId: msg.id || null,
      });
    } catch (e: any) {
      chatError = e?.message || 'could not be saved to the chat';
    }

    try {
      await post('send-notification', {
        uids: [msg.senderUid],
        notification: {
          type: 'message', priority: 'NORMAL',
          title: `Re: ${msg.subject || 'your message'}`,
          preview: text.replace(/\n/g, '<br/>'),
          sender: 'Conncct Team', senderRole: 'Support Team',
          timestamp: new Date().toISOString(), read: false,
        },
        sendEmail: !!msg.senderEmail,
        emails: msg.senderEmail ? [msg.senderEmail] : [],
      });
    } catch (e: any) {
      notifError = e?.message || 'could not be sent as a notification';
    }

    setReplyBusy(false);

    const who = msg.senderName || msg.senderEmail || 'client';
    const byEmail = msg.senderEmail ? ' and by email' : '';

    if (chatError && notifError) {
      showAlert(`Reply not sent: ${notifError}. Nothing has reached ${who} — the text is still in the box, try again.`);
      return;
    }

    setReplyText('');
    if (!chatError && !notifError) {
      showAlert(`Reply sent to ${who} — in their chat, in notifications${byEmail}.`);
    } else if (chatError) {
      showAlert(`Reply sent to ${who} in notifications${byEmail}, but it is not in their chat: ${chatError}.`);
    } else {
      showAlert(`Reply is in ${who}'s chat, but the notification did not go out: ${notifError}. They will see it next time they open Message Us.`);
    }
  };

  const fetchInbox = useCallback(async () => {
    setLoadingInbox(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/admin-inbox`, {
        headers: { Authorization: `Bearer ${getToken() || ''}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInboxMessages(data.messages || []);
      }
    } catch (e) {
      console.error('Error fetching inbox:', e);
    } finally {
      setLoadingInbox(false);
    }
  }, []);

  useEffect(() => { if (activeTab === 'inbox') fetchInbox(); }, [activeTab, fetchInbox]);

  const fmtTime = (iso: string) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffH = (now.getTime() - d.getTime()) / 3600000;
      if (diffH < 1) return `${Math.round(diffH * 60)}m ago`;
      if (diffH < 24) return `${Math.round(diffH)}h ago`;
      if (diffH < 48) return 'Yesterday';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return iso; }
  };

  const priorityColor = (p: string) =>
    p === 'CRITICAL' ? 'bg-red-100 text-red-700' : p === 'HIGH' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700';

  const typeIcon = (t: string) =>
    t === 'alert' ? <AlertCircle className="w-4 h-4 text-red-600" /> :
    t === 'email' ? <Mail className="w-4 h-4 text-purple-600" /> :
    <MessageSquare className="w-4 h-4 text-[var(--brand-d)]" />;

  // ── Toolbar button ──
  const TBtn = ({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) => (
    <button
      type="button" title={title} onMouseDown={e => { e.preventDefault(); onClick(); }}
      className="tbtn"
    >
      {children}
    </button>
  );

  // ── Derived feed data (honestly computed from existing state) ──
  const unreadCount = inboxMessages.filter(m => !readIds.has(m.id)).length;
  const inboxChips = [
    { key: 'all' as const, label: 'All', count: inboxMessages.length },
    { key: 'unread' as const, label: 'Unread', count: unreadCount },
    { key: 'attachments' as const, label: 'Attachments', count: inboxMessages.filter(m => m.attachments?.length > 0).length },
    { key: 'replies' as const, label: 'Replies', count: inboxMessages.filter(m => m.replyToTitle).length },
  ];
  const filteredInbox = inboxMessages.filter(m =>
    inboxFilter === 'unread' ? !readIds.has(m.id) :
    inboxFilter === 'attachments' ? m.attachments?.length > 0 :
    inboxFilter === 'replies' ? !!m.replyToTitle :
    true
  );
  const markAllRead = () => setReadIds(new Set(inboxMessages.map(m => m.id)));

  const sentChips = [
    { key: 'all' as const, label: 'All', count: sentLog.length },
    { key: 'message' as const, label: 'Message', count: sentLog.filter(s => s.type === 'message').length },
    { key: 'alert' as const, label: 'Alert', count: sentLog.filter(s => s.type === 'alert').length },
    { key: 'email' as const, label: 'Email', count: sentLog.filter(s => s.type === 'email').length },
  ];
  const filteredSent = sentLog.filter(s => sentFilter === 'all' ? true : s.type === sentFilter);

  return (
    <AdminLayout
      section="System"
      title="Notification Center"
      icon={Bell}
      subtitle="Send announcements to clients, and read what they send back."
      actions={
        <div className="tabs">
          <button onClick={() => setActiveTab('compose')} className={`tab ${activeTab === 'compose' ? 'active' : ''}`}>
            <Send className="w-3.5 h-3.5" /> Compose
          </button>
          <button onClick={() => setActiveTab('inbox')} className={`tab ${activeTab === 'inbox' ? 'active' : ''}`}>
            <Inbox className="w-3.5 h-3.5" /> Inbox
            {inboxMessages.length > 0 && (
              <span className="cnt">{inboxMessages.length > 9 ? '9+' : inboxMessages.length}</span>
            )}
          </button>
        </div>
      }
    >
      <style>{CSS}</style>

        {/* ════ INBOX TAB ════ */}
        {activeTab === 'inbox' && (
          <div className="ncard">
            <div className="card-h">
              <span className="ic"><Inbox className="w-4 h-4" /></span>
              <b>Messages from Users</b>
              <span className="sub">{inboxMessages.length}</span>
              <span className="spacer" />
              <button onClick={fetchInbox} className="markread" style={{ margin: 0 }}>
                <Clock className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            {/* Filter chips */}
            <div className="chips">
              {inboxChips.map(c => (
                <button key={c.key} onClick={() => setInboxFilter(c.key)}
                  className={`chip ${inboxFilter === c.key ? 'active' : ''}`}>
                  {c.label}<span className="ct">{c.count}</span>
                </button>
              ))}
              <button className="markread" disabled={unreadCount === 0} onClick={markAllRead}>
                <CheckCheck className="w-3.5 h-3.5" /> Mark all as read
              </button>
            </div>

            {loadingInbox ? (
              <div className="empty">
                <div className="ax-spinner" />
                <p style={{ marginTop: 14 }}>Loading inbox…</p>
              </div>
            ) : filteredInbox.length === 0 ? (
              <div className="empty">
                <div className="ei"><Inbox className="w-6 h-6" /></div>
                <b>{inboxMessages.length === 0 ? 'No messages yet' : 'Nothing here'}</b>
                <p>{inboxMessages.length === 0 ? 'Messages from users will appear here' : 'No messages match this filter'}</p>
              </div>
            ) : (
              <div className="feed">
                {groupByDay(filteredInbox, m => m.sentAt).map(grp => (
                  <div key={grp.label}>
                    <div className="daygrp">{grp.label}</div>
                    {grp.items.map((msg: any) => {
                      const isExpanded = expandedMsgId === msg.id;
                      const unread = !readIds.has(msg.id);
                      const hasAtts = msg.attachments?.length > 0;
                      const initials = ((msg.senderName || msg.senderEmail || 'U').split(' ').map((w: string) => w[0]).join('').slice(0, 2)).toUpperCase();
                      return (
                        <div key={msg.id}>
                          {/* Row */}
                          <button
                            onClick={() => {
                              const willExpand = !isExpanded;
                              setExpandedMsgId(isExpanded ? null : msg.id);
                              setReplyText('');
                              if (willExpand) setReadIds(prev => { const n = new Set(prev); n.add(msg.id); return n; });
                            }}
                            className={`nrow ${unread && !isExpanded ? 'unread' : ''} ${isExpanded ? 'open' : ''}`}
                          >
                            <div className={`nicon ${hasAtts ? 'file' : 'msg'}`}>
                              {hasAtts ? <Paperclip className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                            </div>
                            <div className="nmain">
                              <div className="ntop">
                                <span className="nrtitle">{msg.senderName || msg.senderEmail || 'Unknown User'}</span>
                                <span className="ntime"><Clock className="w-3 h-3" />{fmtTime(msg.sentAt)}</span>
                              </div>
                              <div className="nsubj">{msg.subject}</div>
                              {!isExpanded && (
                                <div className="nbody">{(msg.message || '').slice(0, 120)}</div>
                              )}
                              <div className="nmeta">
                                {msg.senderEmail && msg.senderName && (
                                  <span className="ntag">{msg.senderEmail}</span>
                                )}
                                {msg.replyToTitle && (
                                  <span className="ntag blue"><ChevronRight className="w-3 h-3" /> Re: {msg.replyToTitle}</span>
                                )}
                                {hasAtts && (
                                  <span className="ntag"><Paperclip className="w-3 h-3" /> {msg.attachments.length} attachment{msg.attachments.length > 1 ? 's' : ''}</span>
                                )}
                              </div>
                            </div>
                            <ChevronRight className={`nchev ${isExpanded ? 'rot' : ''}`} />
                          </button>

                          {/* Expanded body */}
                          {isExpanded && (
                            <div className="nexp">
                              {/* Sender detail */}
                              <div className="flex items-center gap-3 mb-4 p-3 bg-white rounded-xl border border-gray-200">
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--brand)] to-[var(--brand-d)] flex items-center justify-center text-white text-xs font-black shrink-0">
                                  {initials}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-gray-900">{msg.senderName || 'Unknown'}</p>
                                  <p className="text-xs text-gray-500">{msg.senderEmail} · UID: {msg.senderUid}</p>
                                </div>
                                <div className="ms-auto text-right">
                                  <p className="text-xs text-gray-400">
                                    {msg.sentAt ? new Date(msg.sentAt).toLocaleDateString('en-US', {
                                      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                                      hour: '2-digit', minute: '2-digit',
                                    }) : ''}
                                  </p>
                                </div>
                              </div>

                              {msg.replyToTitle && (
                                <div className="flex items-center gap-2 text-xs text-[var(--brand-d)] font-semibold mb-3">
                                  <ChevronRight className="w-3.5 h-3.5" /> In reply to: {msg.replyToTitle}
                                </div>
                              )}

                              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line mb-4">{msg.message}</p>

                              {hasAtts && (
                                <div className="space-y-2 pt-3 border-t border-gray-100">
                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Attachments</p>
                                  {msg.attachments.map((att: any, ai: number) => {
                                    const isImg = att.type?.startsWith('image/');
                                    const isData = att.url?.startsWith('data:');
                                    return isImg ? (
                                      <div key={ai} className="space-y-1.5">
                                        <img src={att.url} alt={att.name} className="rounded-xl border border-gray-200 max-h-60 object-cover" />
                                        <a href={att.url} download={att.name}
                                          className="flex items-center gap-1.5 text-xs font-semibold text-[var(--brand-d)] hover:text-[var(--brand-d)] transition">
                                          <Download className="w-3.5 h-3.5" /> Download {att.name}
                                        </a>
                                      </div>
                                    ) : (
                                      <a key={ai} href={att.url}
                                        {...(isData ? { download: att.name } : { target: '_blank', rel: 'noopener noreferrer' })}
                                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 hover:border-[var(--brand-line)] transition group">
                                        <div className="w-8 h-8 rounded-lg bg-[var(--brand-soft)] flex items-center justify-center shrink-0">
                                          <Paperclip className="w-4 h-4 text-[var(--brand-d)]" />
                                        </div>
                                        <span className="text-xs font-semibold text-gray-900 truncate group-hover:text-[var(--brand-d)] flex-1">{att.name}</span>
                                        <Download className="w-4 h-4 text-gray-400 group-hover:text-[var(--brand-d)] shrink-0" />
                                      </a>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Reply */}
                              <div className="mt-4 pt-4 border-t border-gray-100">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Reply to {msg.senderName || 'client'}</p>
                                <textarea
                                  value={replyText}
                                  onChange={e => setReplyText(e.target.value)}
                                  rows={3}
                                  placeholder="Write your reply…"
                                  className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand)] focus:border-[var(--brand)] resize-y bg-white"
                                />
                                <div className="flex items-center gap-3 mt-2">
                                  <button
                                    onClick={() => sendReply(msg)}
                                    disabled={replyBusy || !replyText.trim()}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--brand)] text-[var(--brand-on)] text-sm font-bold hover:bg-[var(--brand-h)] disabled:opacity-50"
                                  >
                                    <Send className="w-3.5 h-3.5" /> {replyBusy ? 'Sending…' : 'Send reply'}
                                  </button>
                                  <span className="text-xs text-gray-400 flex items-center gap-1">
                                    <Mail className="w-3.5 h-3.5" /> {msg.senderEmail ? 'Delivered in-app + email' : 'Delivered in-app'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ════ COMPOSE TAB ════ */}
        {activeTab === 'compose' && <div className="space-y-6">

          {/* Stats */}
          <div className="stats cols-3">
            {[
              { label: 'Total Users', value: users.length, icon: <Users className="w-5 h-5" style={{ color: 'var(--brand-d)' }} />, bg: '#EFF6FF' },
              { label: 'Notifications Sent', value: sentLog.reduce((a, b) => a + b.count, 0), icon: <CheckCheck className="w-5 h-5 text-green-600" />, bg: '#F0FDF4' },
              { label: 'Critical Alerts', value: sentLog.filter(s => s.priority === 'CRITICAL').length, icon: <AlertCircle className="w-5 h-5 text-red-600" />, bg: '#FEF2F2' },
            ].map(s => (
              <div key={s.label} className="stat-row">
                <div className="stat-row-ic" style={{ backgroundColor: s.bg }}>{s.icon}</div>
                <div>
                  <div className="stat-row-val">{s.value}</div>
                  <div className="stat-row-lbl">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Compose card */}
          <div className="ncard">
            <div className="card-h">
              <span className="ic"><Send className="w-4 h-4" /></span>
              <b>Compose Notification</b>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 divide-y xl:divide-y-0 xl:divide-x divide-gray-100">

              {/* ── LEFT: Form ── */}
              <div className="p-6 space-y-5">

                {/* Send To */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Send To</label>
                  <div className="flex gap-2">
                    {(['all', 'user'] as const).map(t => (
                      <button key={t}
                        onClick={() => setForm(f => ({ ...f, targetType: t, targetUid: '' }))}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${form.targetType === t ? 'border-[var(--brand)] bg-[var(--brand)] text-[var(--brand-on)]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      >
                        {t === 'all' ? `All Users (${users.length})` : 'Specific User'}
                      </button>
                    ))}
                  </div>
                </div>

                {form.targetType === 'user' && (
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Select User</label>
                    <UserPicker
                      users={users}
                      value={form.targetUid}
                      onChange={id => set('targetUid', id)}
                      loading={loading}
                      ariaLabel="Select the client to notify"
                    />
                  </div>
                )}

                {/* Type & Priority */}
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { label: 'Type', key: 'type' as const, options: [{ v: 'message', l: '💬 Message' }, { v: 'alert', l: '🚨 Alert' }, { v: 'email', l: '📧 Email' }] },
                    { label: 'Priority', key: 'priority' as const, options: [{ v: 'NORMAL', l: '🟢 Normal' }, { v: 'HIGH', l: '🟠 High' }, { v: 'CRITICAL', l: '🔴 Critical' }] },
                  ] as const).map(({ label, key, options }) => (
                    <div key={key}>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{label}</label>
                      <div className="relative">
                        <select value={form[key]} onChange={e => set(key, e.target.value)}
                          className="w-full px-4 py-2.5 pr-9 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)] appearance-none bg-white">
                          {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Emoji + Title */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Smile className="w-3.5 h-3.5" /> Emoji + Title
                  </label>
                  <div className="flex gap-2">
                    <div className="relative">
                      <button type="button" onClick={() => setShowEmojiPicker(p => !p)}
                        className="h-full px-3 border border-gray-300 rounded-lg text-xl hover:bg-gray-50 transition min-w-[52px] flex items-center justify-center">
                        {form.emoji || '😶'}
                      </button>
                      {showEmojiPicker && (
                        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-3 grid grid-cols-6 gap-1.5 w-56">
                          <button onClick={() => { set('emoji', ''); setShowEmojiPicker(false); }}
                            className="col-span-6 text-xs text-gray-400 hover:text-gray-700 text-left mb-1 px-1">
                            ✕ No emoji
                          </button>
                          {EMOJI_PRESETS.map(e => (
                            <button key={e} onClick={() => { set('emoji', e); setShowEmojiPicker(false); }}
                              className={`text-xl p-1.5 rounded-lg hover:bg-gray-100 transition ${form.emoji === e ? 'bg-[var(--brand-soft)] ring-2 ring-[var(--brand)]' : ''}`}>
                              {e}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input type="text" placeholder="Notification title..." value={form.title}
                      onChange={e => set('title', e.target.value)} maxLength={120}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)]" />
                  </div>
                </div>

                {/* Subtitle */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                    Subtitle <span className="font-normal normal-case text-gray-400">(optional)</span>
                  </label>
                  <input type="text" placeholder="A short supporting line..." value={form.subtitle}
                    onChange={e => set('subtitle', e.target.value)} maxLength={160}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)]" />
                </div>

                {/* Rich Message Body */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Message Body</label>
                  {/* Toolbar */}
                  <div className="toolbar">
                    <TBtn title="Bold" onClick={() => execCmd('bold')}><Bold className="w-4 h-4" /></TBtn>
                    <TBtn title="Italic" onClick={() => execCmd('italic')}><Italic className="w-4 h-4" /></TBtn>
                    <TBtn title="Underline" onClick={() => execCmd('underline')}><Underline className="w-4 h-4" /></TBtn>
                    <div className="tsep" />
                    <TBtn title="Bullet List" onClick={() => execCmd('insertUnorderedList')}><List className="w-4 h-4" /></TBtn>
                    <TBtn title="Numbered List" onClick={() => execCmd('insertOrderedList')}><ListOrdered className="w-4 h-4" /></TBtn>
                    <div className="tsep" />
                    <TBtn title="Divider line" onClick={() => execCmd('insertHorizontalRule')}><Minus className="w-4 h-4" /></TBtn>
                    <TBtn title="Clear formatting" onClick={() => execCmd('removeFormat')}>
                      <span className="text-xs font-bold px-1">Tx</span>
                    </TBtn>
                  </div>
                  {/* Editor */}
                  <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={onEditorInput}
                    className="min-h-[140px] px-4 py-3 border border-gray-300 rounded-b-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)] prose prose-sm max-w-none"
                    style={{ lineHeight: 1.6 }}
                    data-placeholder="Write your message here..."
                  />
                </div>

                {/* File / Image Attachments */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5" /> Attachments <span className="font-normal normal-case text-gray-400">(images, PDF, docs)</span>
                  </label>

                  {/* Drop zone */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); Array.from(e.dataTransfer.files).forEach(uploadFile); }}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-4 flex flex-col items-center gap-2 cursor-pointer hover:border-[var(--brand)] hover:bg-[var(--brand-soft)] transition"
                  >
                    <Upload className="w-6 h-6 text-gray-400" />
                    <p className="text-xs text-gray-500 font-medium">Click or drag files here to attach</p>
                    <p className="text-[10px] text-gray-400">Max 20 MB per file</p>
                  </div>
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFilePick}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" />

                  {/* Attached files list */}
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((att, i) => {
                        const isImg = att.type.startsWith('image/');
                        return (
                          <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl">
                            {isImg && att.url && !att.uploading ? (
                              <img src={att.url} alt={att.name}
                                className="w-10 h-10 rounded-lg object-cover border border-gray-200 shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-[var(--brand-soft)] flex items-center justify-center shrink-0">
                                {att.uploading
                                  ? <div className="w-4 h-4 border-2 border-[var(--brand)] border-t-transparent rounded-full animate-spin" />
                                  : <FileText className="w-5 h-5 text-[var(--brand-d)]" />
                                }
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-900 truncate">{att.name}</p>
                              <p className="text-[11px] text-gray-400">
                                {att.uploading ? 'Uploading...' : fmtSize(att.size)}
                              </p>
                            </div>
                            {!att.uploading && (
                              <button onClick={() => removeAttachment(i)}
                                className="text-gray-400 hover:text-red-500 transition shrink-0">
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Link2 className="w-3.5 h-3.5" /> CTA Button <span className="font-normal normal-case text-gray-400">(optional)</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" placeholder='e.g. "View Dashboard"' value={form.ctaLabel}
                      onChange={e => set('ctaLabel', e.target.value)} maxLength={60}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)]" />
                    <input type="url" placeholder="https://..." value={form.ctaUrl}
                      onChange={e => set('ctaUrl', e.target.value)}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)]" />
                  </div>
                </div>

                {/* Sender Identity */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <User2 className="w-3.5 h-3.5" /> Sender Identity
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" placeholder="Sender name" value={form.senderName}
                      onChange={e => set('senderName', e.target.value)}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)]" />
                    <div className="relative">
                      <select value={form.senderRole} onChange={e => set('senderRole', e.target.value)}
                        className="w-full px-4 py-2.5 pr-9 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)] appearance-none bg-white">
                        {SENDER_ROLE_PRESETS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* Also send by email */}
                <label className="flex items-center gap-2.5 mb-3 px-1 cursor-pointer select-none">
                  <input type="checkbox" checked={alsoEmail} onChange={e => setAlsoEmail(e.target.checked)} className="w-4 h-4 rounded border-gray-300 accent-[var(--brand)]" />
                  <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    <Mail className="w-4 h-4 text-gray-400" /> Also send by email
                  </span>
                  <span className="text-xs text-gray-400">delivers a copy to recipients' inboxes</span>
                </label>

                {/* Send */}
                <button onClick={handleSend}
                  disabled={sending || !form.title.trim() || !previewHtml.replace(/<[^>]*>/g, '').trim()}
                  className="w-full py-3 bg-[var(--brand)] text-[var(--brand-on)] rounded-xl text-sm font-bold hover:bg-[var(--brand-h)] transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" />
                  {sending ? 'Sending...' : `Send Notification${form.targetType === 'all' ? ` to All (${users.length})` : ''}`}
                </button>
              </div>

              {/* ── RIGHT: Live Preview ── */}
              <div className="p-6 bg-gray-50">
                <div className="flex items-center gap-2 mb-4">
                  <Eye className="w-4 h-4 text-gray-400" />
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Live Preview</p>
                </div>

                {/* List card preview */}
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Notification List</p>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex relative mb-5"
                  style={{ borderLeft: `4px solid ${pc.border}` }}>
                  <div className="flex-shrink-0 flex items-start pt-4 pl-4 pr-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: iconStyle.bg, color: iconStyle.color }}>
                      {TYPE_ICON_EL[form.type]}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 py-4 pr-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-md shrink-0"
                          style={{ backgroundColor: pc.bg, color: pc.color }}>{form.priority}</span>
                        <span className="text-sm font-bold text-gray-900 truncate">
                          {fullTitle || <span className="text-gray-300">Title appears here</span>}
                        </span>
                      </div>
                      <span className="text-[11px] text-gray-400 shrink-0">Just now</span>
                    </div>
                    <p className="text-xs font-semibold text-gray-500 mb-1">
                      {form.senderName || 'Admin'} <span className="font-normal">• {form.senderRole}</span>
                    </p>
                    {previewHtml && (
                      <p className="text-xs text-gray-600 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    )}
                  </div>
                  <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-[var(--brand)]" />
                </div>

                {/* Detail preview */}
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">Notification Detail</p>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-5 pt-5 pb-0">
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: pc.bg, color: pc.color }}>{pc.label}</span>
                    <span className="text-[11px] text-gray-400">Just now</span>
                  </div>
                  <div className="px-5 pt-3 pb-4 border-b border-slate-100">
                    <h3 className="text-lg font-black text-gray-900">
                      {fullTitle || <span className="text-gray-300">Notification title</span>}
                    </h3>
                    {form.subtitle && <p className="text-sm text-gray-500 mt-1">{form.subtitle}</p>}
                  </div>
                  <div className="mx-5 mt-4 mb-4 rounded-xl flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[var(--brand)] flex items-center justify-center text-[var(--brand-on)] text-xs font-black shrink-0">
                        {senderInitials}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{form.senderName || 'Admin'}</p>
                        <p className="text-xs text-gray-500">{form.senderRole}</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-black text-[var(--brand-d)] border border-[var(--brand-line)] bg-[var(--brand-soft)] px-2 py-0.5 rounded-full">VERIFIED ADMIN</span>
                  </div>
                  <div className="px-5 pb-4">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
                      <span style={{ color: iconStyle.color }}>{TYPE_ICON_EL[form.type]}</span>
                      <span>{form.type}</span>
                    </div>
                    {previewHtml ? (
                      <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    ) : (
                      <p className="text-sm text-gray-300">Message body will appear here...</p>
                    )}
                    {/* Attachments preview */}
                    {attachments.filter(a => !a.uploading).length > 0 && (
                      <div className="mt-3 space-y-2">
                        {attachments.filter(a => !a.uploading).map((att, i) => {
                          const isImg = att.type.startsWith('image/');
                          return isImg ? (
                            <img key={i} src={att.url} alt={att.name}
                              className="w-full rounded-xl border border-gray-200 object-cover max-h-40" />
                          ) : (
                            <div key={i} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                              <FileText className="w-4 h-4 text-[var(--brand-d)] shrink-0" />
                              <span className="text-xs font-medium text-gray-700 truncate">{att.name}</span>
                              <span className="text-[11px] text-gray-400 shrink-0">{fmtSize(att.size)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 px-5 pb-5 pt-3 border-t border-slate-100">
                    {form.ctaLabel && form.ctaUrl && (
                      <span className="px-4 py-2 rounded-xl bg-[var(--brand)] text-[var(--brand-on)] text-xs font-bold">{form.ctaLabel}</span>
                    )}
                    <span className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700">Reply to Admin</span>
                    <span className="px-4 py-2 rounded-xl bg-[var(--brand)] text-[var(--brand-on)] text-xs font-bold">Acknowledge Receipt</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sent Log + User Directory */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* ── Sent Log feed ── */}
            <div className="ncard">
              <div className="card-h">
                <span className="ic"><MessageSquare className="w-4 h-4" /></span>
                <b>Sent Log</b>
                <span className="sub">{sentLog.reduce((a, b) => a + b.count, 0)} delivered</span>
              </div>
              <div className="chips">
                {sentChips.map(c => (
                  <button key={c.key} onClick={() => setSentFilter(c.key)}
                    className={`chip ${sentFilter === c.key ? 'active' : ''}`}>
                    {c.label}<span className="ct">{c.count}</span>
                  </button>
                ))}
              </div>
              {filteredSent.length === 0 ? (
                <div className="empty">
                  <div className="ei"><Bell className="w-6 h-6" /></div>
                  <b>{sentLog.length === 0 ? 'No notifications sent yet' : 'Nothing here'}</b>
                  <p>{sentLog.length === 0 ? 'Notifications you broadcast will be logged here' : 'No notifications match this filter'}</p>
                </div>
              ) : (
                <div className="feed" style={{ maxHeight: 460, overflowY: 'auto' }}>
                  {groupByDay(filteredSent, l => l.sentAt).map(grp => (
                    <div key={grp.label}>
                      <div className="daygrp">{grp.label}</div>
                      {grp.items.map((log, i) => (
                        <div key={i} className="nrow plain">
                          <div className={`nicon ${log.type === 'alert' ? 'alert' : log.type === 'email' ? 'email' : 'msg'}`}>
                            {typeIcon(log.type)}
                          </div>
                          <div className="nmain">
                            <div className="ntop">
                              <span className="nrtitle">{log.title}</span>
                              <span className="ntime">{new Date(log.sentAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <div className="nbody">{log.message}</div>
                            <div className="nmeta">
                              <span className={`prio ${log.priority}`}>{log.priority}</span>
                              <span className="ntag">→ {log.to}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── User Directory ── */}
            <div className="ncard">
              <div className="card-h">
                <span className="ic"><Users className="w-4 h-4" /></span>
                <b>User Directory</b>
                <span className="sub">{users.length}</span>
                <span className="spacer" />
                <div className="ax-search">
                  <Search />
                  <input type="text" placeholder="Search..." onChange={e => setUserSearch(e.target.value)} />
                </div>
              </div>
              <div className="card-b">
                {loading ? (
                  <div className="empty"><div className="ax-spinner" /></div>
                ) : (
                  <div className="udir">
                    {filteredUsers.slice(0, 30).map(u => (
                      <div key={u._id || u.uid} className="urow">
                        <div className="uav">{u.firstName?.[0]}{u.lastName?.[0]}</div>
                        <div className="min-w-0">
                          <div className="un">{u.firstName} {u.lastName}</div>
                          <div className="ue">{u.email}</div>
                        </div>
                      </div>
                    ))}
                    {filteredUsers.length > 30 && (
                      <div className="umore">+{filteredUsers.length - 30} more</div>
                    )}
                    {filteredUsers.length === 0 && !loading && (
                      <div className="umore">No users found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>}

    </AdminLayout>
  );
}
